import React, { useState } from 'react';
import { Box, TextField, Button } from '@mui/material';
const apiURL = process.env.REACT_APP_API_URL || 'http://localhost:7071';

export default function PropertyForm({ initialData = {}, onSave, onCancel }) {
  const [form, setForm] = useState(initialData || {});
  const [previewUrl, setPreviewUrl] = useState(initialData?.logo || '');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedImageIndexes, setSelectedImageIndexes] = useState([]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const uploadRes = await fetch(`${apiURL}/api/property/logo-upload`, {
        method: 'POST',
        body: formData
      });
      const result = await uploadRes.json();
      if (uploadRes.ok && result.url) {
        setForm(prev => ({ ...prev, logo: result.url }));
        setPreviewUrl(result.url);
      } else {
        console.error('❌ Logo upload failed:', result.error);
      }
    } catch (err) {
      console.error('❌ Upload error:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const newImage = { uploading: true };
    setForm(prev => ({
      ...prev,
      images: [...(prev.images || []), newImage]
    }));
    const index = (form.images?.length || 0);

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await fetch(`${apiURL}/api/property/upload-image`, {
        method: 'POST',
        body: formData
      });
      const result = await res.json();
      if (res.ok && result.url) {
        setForm(prev => {
          const updated = [...(prev.images || [])];
          updated[index] = result.url;
          return { ...prev, images: updated };
        });
      } else {
        console.error('❌ Upload failed:', result.error);
      }
    } catch (err) {
      console.error('❌ Upload error:', err);
    }
  };

  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const propertyId = user?.propertyId?.id || user?.properties?.[0]?.propertyId?.id;
    const data = new FormData();
    for (const key in form) {
      if (key === 'images') {
        data.append(key, JSON.stringify(form[key]));
      } else if (key === 'socialLinks') {
        const { facebook, instagram, website, whatsapp } = form[key] || {};
        data.append('socialLinks.facebook', facebook || '');
        data.append('socialLinks.instagram', instagram || '');
        data.append('socialLinks.website', website || '');
        data.append('socialLinks.whatsapp', whatsapp || '');
      } else {
        data.append(key, form[key]);
      }
    }
    
    if (propertyId) {
      data.append('propertyId', propertyId); // ✅ append directly to FormData
    } else {
      console.error('❌ No propertyId found in localStorage');
      return;
    }

    try {
      const response = await fetch(`${apiURL}/api/property`, {
        method: 'POST',
        body: data
      });
      const result = await response.json();
      console.log('📬 Server response:', result);
      if (!response.ok) throw new Error(result.error || 'Failed to save');

      // Fetch updated property and apply to form
      const resProp = await fetch(`${apiURL}/api/property?propertyId=${propertyId}`);
      const updatedProperty = await resProp.json();
      setForm({
        ...updatedProperty,
        socialLinks: {
          facebook: updatedProperty.socialLinks?.facebook || '',
          instagram: updatedProperty.socialLinks?.instagram || '',
          website: updatedProperty.socialLinks?.website || '',
          whatsapp: updatedProperty.socialLinks?.whatsapp || ''
        }
      }); // if setFormData exists
      setPreviewUrl(updatedProperty.logo || ''); // ✅ Update preview with Cloudinary logo
      if (onSave) onSave(form);

    } catch (err) {
      console.error('❌ Upload failed:', err);
    }
  };

  return (
    <Box p={2} display="flex" flexDirection="column" gap={2}>
      <Box display="flex" gap={4}>
        <Box>
          <label htmlFor="logo">Logo</label>
          <input
            id="logo"
            name="logo"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'block', marginTop: '8px' }}
          />
          {previewUrl && (
            <Box mt={1} sx={{ position: 'relative', display: 'inline-block' }}>
              <img src={previewUrl} alt="Logo Preview" style={{ width: 120, height: 80, borderRadius: 4 }} />
              {uploadingLogo && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(255,255,255,0.8)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  Uploading...
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box>
          <label htmlFor="feature">Feature Photo</label>
          <input
            id="feature"
            name="feature"
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'block', marginTop: '8px' }}
          />
          {form.images?.length > 0 && (
            <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
              {form.images.map((file, idx) => {
                const isUploading = file.uploading;
                const imageUrl =
                  typeof file === 'string'
                    ? file
                    : file instanceof File
                      ? URL.createObjectURL(file)
                      : '';
                return (
                  <Box key={idx} sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={imageUrl}
                      alt={`Image ${idx}`}
                      style={{
                        width: 120,
                        height: 80,
                        borderRadius: 4,
                        opacity: isUploading ? 0.5 : 1,
                        cursor: 'pointer',
                        outline: selectedImageIndexes.includes(idx) ? '2px solid #007aff' : 'none'
                      }}
                      onClick={() => {
                        if (isUploading) return;
                        setSelectedImageIndexes(prev =>
                          prev.includes(idx)
                            ? prev.filter(i => i !== idx)
                            : [...prev, idx]
                        );
                      }}
                    />
                    {isUploading && (
                      <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(255,255,255,0.8)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        Uploading...
                      </Box>
                    )}
                    {!isUploading && (
                      <Box
                        onClick={async () => {
                          const imageToDelete = form.images[idx];
                          const updatedImages = form.images.filter((_, i) => i !== idx);
                          setForm(prev => ({
                            ...prev,
                            images: updatedImages
                          }));
                          if (typeof imageToDelete === 'string') {
                            await fetch(`${apiURL}/api/rooms/image?filename=${encodeURIComponent(imageToDelete)}`, {
                              method: 'DELETE'
                            });
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          minWidth: 'auto',
                          padding: '4px',
                          lineHeight: 1,
                          background: 'white',
                          borderRadius: '50%',
                          color: 'red',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        ✖
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
      <TextField label="Name" value={form.name || ''} onChange={e => handleChange('name', e.target.value)} fullWidth />
      <TextField label="Address" value={form.address || ''} onChange={e => handleChange('address', e.target.value)} fullWidth />
      <TextField label="Email" value={form.email || ''} onChange={e => handleChange('email', e.target.value)} fullWidth />
      <TextField label="Phone" value={form.phone || ''} onChange={e => handleChange('phone', e.target.value)} fullWidth />
      <TextField label="Description" value={form.description || ''} onChange={e => handleChange('description', e.target.value)} fullWidth multiline rows={3} />
      <TextField label="Google Map Embed Link" value={form.googleMapEmbed || ''} onChange={e => handleChange('googleMapEmbed', e.target.value)} fullWidth />
      <TextField
        label="Google Map Link"
        value={form.googleMapLink || ''}
        onChange={e => handleChange('googleMapLink', e.target.value)}
        fullWidth
      />
      
      <TextField
        label="Facebook"
        type="url"
        value={form.socialLinks?.facebook || ''}
        onChange={e =>
          setForm(prev => ({
            ...prev,
            socialLinks: {
              ...prev.socialLinks,
              facebook: e.target.value.trim()
            }
          }))
        }
        fullWidth
      />
      <TextField
        label="Instagram"
        type="url"
        value={form.socialLinks?.instagram || ''}
        onChange={e =>
          setForm(prev => ({
            ...prev,
            socialLinks: {
              ...prev.socialLinks,
              instagram: e.target.value.trim()
            }
          }))
        }
        fullWidth
      />

<TextField
        label="WhatsApp"
        type="url"
        value={form.socialLinks?.whatsapp || ''}
        onChange={e =>
          setForm(prev => ({
            ...prev,
            socialLinks: {
              ...prev.socialLinks,
              whatsapp: e.target.value.trim()
            }
          }))
        }
        fullWidth
      />
      <TextField
        label="Website"
        type="url"
        value={form.socialLinks?.website || ''}
        onChange={e =>
          setForm(prev => ({
            ...prev,
            socialLinks: {
              ...prev.socialLinks,
              website: e.target.value.trim()
            }
          }))
        }
        fullWidth
      />
     

      <TextField
        label="Hotel Star Rating"
        type="number"
        inputProps={{ step: 0.1, min: 0, max: 5 }}
        value={form.hotelStarRating || ''}
        onChange={e => handleChange('hotelStarRating', parseFloat(e.target.value))}
      />

      <Button onClick={handleSubmit} variant="contained">Save</Button>
      <Button onClick={onCancel}>Cancel</Button>
    </Box>
  );
}